import { selectEnterprise } from 'base/core/selectors';
import { IRestApiResponse } from 'interfaces';
import {
  all, call, put, select, takeLatest,
} from 'redux-saga/effects';
import { request } from 'utils/request';
import { API } from './constants';
import {
  ExpertContent,
  IMediaDelete,
  IMediaRequestDto,
  IMediaUpdatePayload,
  MediaLibraryState,
  MediaUploadPayload,
  SaveDownloadVideoTrimmed,
  Sort,
} from './interface';
import { selectMediaState } from './selector';
import { mediaLibraryAction } from './slice';

export function* fetchMediaLibrary(): any {
  try {
    const mediaState: MediaLibraryState = yield select(selectMediaState);
    const enterprise = yield select(selectEnterprise);

    const data: IMediaRequestDto = {
      page: mediaState.page + 1,
      limit: mediaState.perPage,
      path: mediaState.path,
      keyword: mediaState.filters.keyword,
      type: mediaState.filters.type,
    };

    if (mediaState.filters.keyword) {
      data.keyword = mediaState.filters.keyword;
    }

    if (mediaState.filters.type.includes(Sort.UPLOADED)) {
      data.ownership = Sort.UPLOADED;
    }

    if (mediaState.filters.type.includes(Sort.PURCHASED)) {
      data.ownership = Sort.PURCHASED;
    }
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.GET_FETCH_MEDIA.path}/${enterprise.id}?keyword=${data.keyword}&path=${data.path}&type=${data.type}&page=${data.page}&limit=${data.limit}&sortBy=${mediaState.filters.sortBy}`,
        method: API.GET_FETCH_MEDIA.method,
      },
      {},
      true,
      false,
    );
    if (response.statusCode === 200) {
      yield put(mediaLibraryAction.setCount(response.data.total));
      yield put(mediaLibraryAction.deselectAllFiles());
      yield put(mediaLibraryAction.fetchMediaSucceed(response.data));
    } else {
      yield put(mediaLibraryAction.fetchMediaFailed());
    }
  } catch (error) {
    yield put(mediaLibraryAction.fetchMediaFailed());
  }
}

export function* fetchRemainingStroage(): any {
  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.GET_REMAINING_STROAGE.path}`,
        method: API.GET_REMAINING_STROAGE.method,
      },
      {},
      true,
      false,
    );
    if (response.statusCode === 200) {
      yield put(mediaLibraryAction.fetchRemainingStroageSucceed(response.data));
    } else {
      yield put(mediaLibraryAction.fetchRemainingStroageFailed());
    }
  } catch (error) {
    yield put(mediaLibraryAction.fetchRemainingStroageFailed());
  }
}

export function* createMediaContent({ payload }: MediaUploadPayload): any {
  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.POST_UPLOAD_MEDIA.path}`,
        method: API.POST_UPLOAD_MEDIA.method,
      },
      { ...payload },
      true,
      false,
    );
    if (response.statusCode === 201) {
      yield put(mediaLibraryAction.uploadMediaSucceed(response.data));
      yield payload.resolve();
      yield put(mediaLibraryAction.fetchMedia());
    } else {
      yield put(mediaLibraryAction.uploadMediaFailed());
      yield payload.reject();
    }
  } catch (error) {
    yield put(mediaLibraryAction.uploadMediaFailed());
    yield payload.reject();
  }
}

export function* deleteSelectedMedia(): any {
  try {
    const mediaState = yield select(selectMediaState);

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < mediaState.selectedFiles.length; i++) {
      const file = mediaState.media.find(
        (f: ExpertContent) => f._id === mediaState.selectedFiles[i],
      )!;

      const data = {
        id: file!._id,
        contentUrl: file.url ? file.url : null,
        fileSize: file.fileSize,
      };

      yield call(
        request,
        {
          path: `${API.DELETE_DELETE_MEDIA.path}`,
          method: API.DELETE_DELETE_MEDIA.method,
        },
        data,
        true,
        false,
      );
    }

    yield put(mediaLibraryAction.deleteSelectedMediaSucceed());
    yield put(mediaLibraryAction.deselectAllFiles());
    yield put(mediaLibraryAction.fetchMedia());
  } catch (error) {
    yield put(mediaLibraryAction.deleteSelectedMediaFailed());
  }
}

function* deleteMedia({ payload }: IMediaDelete): any {
  const mediaState = yield select(selectMediaState);

  const data = {
    id: payload.id,
    contentUrl: payload.contentUrl,
    fileSize: payload.fileSize,
  };

  try {
    const isMediaSelected = mediaState.selectedFiles.includes(payload.id);

    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.DELETE_DELETE_MEDIA.path}`,
        method: API.DELETE_DELETE_MEDIA.method,
      },
      data,
      true,
      false,
    );

    if (response.statusCode === 201) {
      yield put(mediaLibraryAction.deleteMediaSucceed(response.data));
    } else {
      yield put(mediaLibraryAction.deleteMediaFailed());
    }

    if (isMediaSelected) {
      yield put(mediaLibraryAction.toggleSelectedFile(payload.id));
    }

    yield put(mediaLibraryAction.fetchMedia());
  } catch (error) {
    yield put(mediaLibraryAction.deleteMediaFailed());
  }
}

function* updateMedia({ payload }: IMediaUpdatePayload): any {
  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.POST_UPDATE_MEDIA.path}`,
        method: API.POST_UPDATE_MEDIA.method,
      },
      payload,
      true,
      false,
    );
    if (response.statusCode === 201) {
      yield put(mediaLibraryAction.updateMediaSucceed(response.data));
      yield put(mediaLibraryAction.fetchMedia());
    }
  } catch (error) {
    yield put(mediaLibraryAction.updateMediaFailed());
  }
}

export function* saveDownloadTrimmedVideoGenerator({ payload }: SaveDownloadVideoTrimmed) {
  try {
    const response: IRestApiResponse = yield call(
      request,
      { path: `${API.POST_VIDEO_TRIM.path}`, method: API.POST_VIDEO_TRIM.method },
      payload,
    );

    if (response.statusCode === 201) {
      yield put(mediaLibraryAction.saveDownloadTrimmedVideoSuccess());
      yield payload.resolve();
    } else {
      yield put(mediaLibraryAction.saveDownloadTrimmedVideoFailed());
      yield payload.reject();
    }
  } catch (error) {
    yield put(mediaLibraryAction.saveDownloadTrimmedVideoFailed());
    yield payload.reject();
  }
}

export function* mediaSaga() {
  yield all([
    takeLatest(mediaLibraryAction.setPage.type, fetchMediaLibrary),
    takeLatest(mediaLibraryAction.setPerPage.type, fetchMediaLibrary),
    takeLatest(mediaLibraryAction.setKeyword.type, fetchMediaLibrary),
    takeLatest(mediaLibraryAction.setType.type, fetchMediaLibrary),
    takeLatest(mediaLibraryAction.setSortBy.type, fetchMediaLibrary),
    takeLatest(mediaLibraryAction.clearFilters.type, fetchMediaLibrary),
    takeLatest(mediaLibraryAction.setPath.type, fetchMediaLibrary),
    takeLatest(mediaLibraryAction.fetchMedia.type, fetchMediaLibrary),
    takeLatest(mediaLibraryAction.fetchRemainingStroage.type, fetchRemainingStroage),
    takeLatest(mediaLibraryAction.uploadMedia.type, createMediaContent),
    takeLatest(mediaLibraryAction.deleteSelectedMedia.type, deleteSelectedMedia),
    takeLatest(mediaLibraryAction.deleteMedia.type, deleteMedia),
    takeLatest(mediaLibraryAction.updateMedia.type, updateMedia),
    takeLatest(mediaLibraryAction.saveDownloadTrimmedVideo.type, saveDownloadTrimmedVideoGenerator),
  ]);
}

export default mediaSaga;
